// src/components/BunnyCharacter.tsx
import { motion, type TargetAndTransition } from 'framer-motion'

type AnimateState = 'idle' | 'bounce' | 'celebrate' | 'think' | 'challenge' | 'wrong' | 'laugh'

interface BunnyCharacterProps {
  size?: 'sm' | 'md' | 'lg'
  animate?: AnimateState
  animationKey?: string | number   // increment to re-trigger one-shot animations
}

const sizeMap = { sm: 'text-5xl', md: 'text-7xl', lg: 'text-9xl' }

// Whole-body motion for the wrapper div
const bodyAnimations: Record<AnimateState, TargetAndTransition> = {
  idle: {
    y: [0, -5, 0],
    transition: { repeat: Infinity, duration: 2, ease: 'easeInOut' },
  },
  bounce: {
    y: [0, -20, 0],
    transition: { repeat: Infinity, duration: 0.6, ease: 'easeOut' },
  },
  celebrate: {
    rotate: [0, -10, 10, -10, 0],
    scale: [1, 1.3, 1],
    transition: { repeat: Infinity, duration: 0.7 },
  },
  think: {
    rotate: [0, 5, 0, -5, 0],
    transition: { repeat: Infinity, duration: 3, ease: 'easeInOut' },
  },
  challenge: {
    y: [0, -8, 0],
    rotate: [0, -3, 3, 0],
    transition: { repeat: Infinity, duration: 1.8, ease: 'easeInOut' },
  },
  wrong: {
    x: [0, -12, 12, -10, 10, -6, 6, 0],
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
  laugh: {
    y: [0, -15, 0, -10, 0],
    rotate: [0, 8, -8, 5, 0],
    scale: [1, 1.15, 1],
    transition: { repeat: Infinity, duration: 0.9, ease: 'easeInOut' },
  },
}

// Left ear animations
const leftEarAnimations: Record<AnimateState, TargetAndTransition> = {
  idle:      {},
  bounce:    {},
  celebrate: { rotate: [0, -20, 20, 0], transition: { repeat: Infinity, duration: 0.7 } },
  think:     { rotate: [0, -10, 0],     transition: { repeat: Infinity, duration: 3 } },
  challenge: { rotate: [0, -25, 0, -15, 0], transition: { repeat: Infinity, duration: 1.8 } },
  wrong:     { rotate: [0, -30, 0],     transition: { duration: 0.5 } },
  laugh:     { rotate: [0, -20, 20, -10, 0], transition: { repeat: Infinity, duration: 0.9 } },
}

// Right ear animations (mirror of left)
const rightEarAnimations: Record<AnimateState, TargetAndTransition> = {
  idle:      {},
  bounce:    {},
  celebrate: { rotate: [0, 20, -20, 0],  transition: { repeat: Infinity, duration: 0.7 } },
  think:     { rotate: [0, 10, 0],       transition: { repeat: Infinity, duration: 3 } },
  challenge: { rotate: [0, 25, 0, 15, 0], transition: { repeat: Infinity, duration: 1.8, delay: 0.2 } },
  wrong:     { rotate: [0, 30, 0],       transition: { duration: 0.5 } },
  laugh:     { rotate: [0, 20, -20, 10, 0], transition: { repeat: Infinity, duration: 0.9, delay: 0.1 } },
}

// Eye scale for expressions
const eyeAnimations: Record<AnimateState, TargetAndTransition> = {
  idle:      {},
  bounce:    {},
  celebrate: {},
  think:     { scaleY: [1, 0.4, 1], transition: { repeat: Infinity, duration: 3 } },
  challenge: { scaleY: [1, 1.3, 1], scaleX: [1, 1.2, 1], transition: { repeat: Infinity, duration: 1.8 } },
  wrong:     { scaleY: [1, 0.2, 1], transition: { duration: 0.3 } },
  laugh:     { scaleY: [1, 0.5, 1, 0.5, 1], transition: { repeat: Infinity, duration: 0.9 } },
}

export function BunnyCharacter({ size = 'md', animate = 'idle', animationKey }: BunnyCharacterProps) {
  return (
    <motion.div
      key={animationKey}
      animate={bodyAnimations[animate]}
      className={`select-none ${sizeMap[size]}`}
      role="img"
      aria-label="Påskharen"
    >
      <svg viewBox="0 0 120 140" width="1em" height="1em" fill="none">
        {/* Body */}
        <ellipse cx="60" cy="105" rx="35" ry="30" fill="#F5E6D3" />
        {/* Head */}
        <circle cx="60" cy="70" r="25" fill="#F5E6D3" />

        {/* Left ear - animated individually */}
        <motion.g
          style={{ transformOrigin: '45px 48px' }}
          animate={leftEarAnimations[animate]}
        >
          <ellipse cx="45" cy="30" rx="10" ry="28" fill="#F5E6D3" />
          <ellipse cx="45" cy="30" rx="6"  ry="20" fill="#FFB6C1" />
        </motion.g>

        {/* Right ear - animated individually */}
        <motion.g
          style={{ transformOrigin: '75px 48px' }}
          animate={rightEarAnimations[animate]}
        >
          <ellipse cx="75" cy="30" rx="10" ry="28" fill="#F5E6D3" />
          <ellipse cx="75" cy="30" rx="6"  ry="20" fill="#FFB6C1" />
        </motion.g>

        {/* Left eye */}
        <motion.g animate={eyeAnimations[animate]} style={{ transformOrigin: '50px 65px' }}>
          <circle cx="50" cy="65" r="4" fill="#2D3436" />
          <circle cx="51" cy="63" r="1.5" fill="white" />
        </motion.g>
        {/* Right eye */}
        <motion.g animate={eyeAnimations[animate]} style={{ transformOrigin: '70px 65px' }}>
          <circle cx="70" cy="65" r="4" fill="#2D3436" />
          <circle cx="71" cy="63" r="1.5" fill="white" />
        </motion.g>

        {/* Nose */}
        <ellipse cx="60" cy="73" rx="3" ry="2.5" fill="#FFB6C1" />
        {/* Mouth */}
        <path d="M55 78 Q60 83 65 78" stroke="#2D3436" strokeWidth="1.5" fill="none" />
        {/* Cheeks */}
        <circle cx="42" cy="73" r="5" fill="#FFB6C1" opacity="0.3" />
        <circle cx="78" cy="73" r="5" fill="#FFB6C1" opacity="0.3" />
        {/* Belly */}
        <ellipse cx="60" cy="108" rx="20" ry="18" fill="white" opacity="0.5" />
        {/* Feet */}
        <ellipse cx="42" cy="130" rx="12" ry="6" fill="#F5E6D3" />
        <ellipse cx="78" cy="130" rx="12" ry="6" fill="#F5E6D3" />
      </svg>
    </motion.div>
  )
}
